import {useTranslation} from '@aboutyou/i18n';
import {PageTypes, useCurrentPageType} from '@aboutyou/router';
import {
  AdjustTracker,
  useTrackAdjustImpression,
  useTrackFeature,
} from '@aboutyou/tracking';
import {useInViewEffect} from '@aboutyou/ui-components';
import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import bannerBackground from '../Coupons/assets/bannerBackground.svg';
import {
  AdjustDeepLink,
  useGetStoreLink,
} from '../StoreBadges/utils/useGetStoreLink';
import {
  WEB_COUPON_DISCOUNT,
  WebCouponBanner,
  WebCouponTranslations,
} from './WebCouponBanner';

export const SimpleWebCouponWrapper: React.FC<{
  inOffCanvas?: boolean;
  className?: string;
}> = ({inOffCanvas, className}) => {
  const {t} = useTranslation();
  const pageType = useCurrentPageType();
  const trackFeature = useTrackFeature();
  const trackFeatureLabel = getTrackFeatureLabel({inOffCanvas, pageType});
  const adjustTracker = getAdjustTracker({inOffCanvas, pageType});

  const trackAdjustImpression = useTrackAdjustImpression({
    adjustTracker,
  });

  const onCTAClick = () => {
    trackFeature({eventType: 'click', label: trackFeatureLabel});
  };
  const {ref} = useInViewEffect<HTMLDivElement>(
    useCallback(inView => {
      if (inView) {
        trackAdjustImpression();

        trackFeature({
          eventType: 'impression',
          label: trackFeatureLabel,
        });
      }
    }, []),
    {triggerOnce: true},
  );

  const CTALink = useGetStoreLink({
    adjustTracker,
    deepLink: AdjustDeepLink.CouponWallet,
  });

  const translations: WebCouponTranslations = useMemo(
    () => ({
      text: t(
        'Get your <b>{discount} Welcome Coupon for new App customers</b> and many personalized offers.',
        {discount: WEB_COUPON_DISCOUNT},
      ),
      ctaText: t('Download App'),
      closeButton: t('Close'),
      modalBadgeText: t('Coupons'),
      modalTitle: t('Welcome to the App Coupon'),
      modalContent: t('WebCouponBannerInfoText'),
    }),
    [t],
  );

  return (
    <StyledWebCouponBanner
      className={className}
      ref={ref}
      translations={translations}
      textSize="px12"
      CTALink={CTALink}
      onCTAClick={onCTAClick}
      bannerBackgroundSvg={bannerBackground}
    />
  );
};

const getTrackFeatureLabel = ({
  inOffCanvas,
  pageType,
}: {
  inOffCanvas?: boolean;
  pageType?: PageTypes;
}) => {
  if (inOffCanvas) {
    return 'generic_coupon_app_banner_offCanvas';
  }

  return pageType === 'category'
    ? 'generic_coupon_app_banner_category'
    : 'generic_coupon_app_banner_browse';
};

const getAdjustTracker = ({
  inOffCanvas,
  pageType,
}: {
  inOffCanvas?: boolean;
  pageType?: PageTypes;
}) => {
  if (inOffCanvas) {
    return AdjustTracker.MWEB_WEB_COUPON_BANNER_OFF_CANVAS;
  }

  return pageType === 'home'
    ? AdjustTracker.MWEB_WEB_COUPON_BANNER_BROWSE_PAGE
    : AdjustTracker.MWEB_WEB_COUPON_BANNER_CATEGORY_PAGE;
};

const StyledWebCouponBanner = styled(WebCouponBanner)`
  display: var(--web-coupon-card-display-correction, flex);
  max-height: 126px;
`;
